/**
 * The stage's bottom-left cluster: back, `3 of 5`, forward (Beka, 2026-09-25).
 *
 * The zoom cluster's twin in the opposite corner, built from the same parts: a 36px bordered bar on the
 * overlay ground, 28px icon slots, and a readout between them. The readout is a count, not a control, so
 * unlike the zoom readout it takes no press.
 *
 * The arrow with nowhere to go is DISABLED (Beka, 2026-09-25): back on the first, forward on the last.
 * `ZoomCluster` keeps its buttons live because a disabled button drops the keyboard's focus; here the
 * press that reaches an end hands the focus to the other arrow instead, so the keyboard keeps its place.
 */
import React, { useEffect, useRef } from 'react';
import { IconSlot } from '~/design-system/components/v1_5/Header';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';
import { cn } from '~/lib/utils';
import styles from './PagerCluster.module.css';
import type { HeaderTheme } from './theme';

export interface PagerClusterProps {
  /** The open item's place in the list, from 0. */
  index: number;
  total: number;
  /** A press on an arrow: -1 back, 1 forward. */
  onStep: (dir: -1 | 1) => void;
  /** What one item is called, for assistive tech: `Artifact 3 of 5`. */
  noun?: string;
  /** Distance from the stage's bottom edge, the zoom cluster's. */
  bottom?: number;
  theme?: HeaderTheme;
  className?: string;
}

export function PagerCluster({
  index,
  total,
  onStep,
  noun = 'Item',
  bottom = 16,
  theme: themeProp,
  className,
}: PagerClusterProps) {
  const theme = useV15Theme(themeProp);
  const atStart = index <= 0;
  const atEnd = index >= total - 1;
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  /** Which arrow was pressed while it held the focus: if that press reached an end, the focus moves on. */
  const pressed = useRef<'prev' | 'next' | null>(null);
  useEffect(() => {
    const which = pressed.current;
    pressed.current = null;
    if (which === 'next' && atEnd) prevRef.current?.focus();
    if (which === 'prev' && atStart) nextRef.current?.focus();
  }, [index, atStart, atEnd]);
  const step = (dir: -1 | 1) => {
    const button = dir < 0 ? prevRef.current : nextRef.current;
    pressed.current = button && document.activeElement === button ? (dir < 0 ? 'prev' : 'next') : null;
    onStep(dir);
  };
  return (
    <div
      className={cn(themes[theme], styles.cluster, className)}
      style={{ '--hv15-pager-bottom': `${bottom}px` } as React.CSSProperties}
    >
      <IconSlot
        ref={prevRef}
        icon="arrow-left-s"
        glyphSize={15}
        label="Previous"
        theme={theme}
        disabled={atStart}
        onClick={() => step(-1)}
        className={styles.button}
      />
      <span className={styles.readout} aria-label={`${noun} ${index + 1} of ${total}`}>
        {index + 1} of {total}
      </span>
      <IconSlot
        ref={nextRef}
        icon="arrow-right-s"
        glyphSize={15}
        label="Next"
        theme={theme}
        disabled={atEnd}
        onClick={() => step(1)}
        className={styles.button}
      />
    </div>
  );
}
